import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { SectorBadge } from '@/components/ui/sector-badge';
import { CompanyCompleteness } from '@/components/profile/CompanyCompleteness';
import { AccountInfoCard } from '@/components/profile/AccountInfoCard';
import { CompanyStatsCard } from '@/components/profile/CompanyStatsCard';
import { CompanyLogoUpload } from '@/components/profile/CompanyLogoUpload';
import { CompanyQuickLinksCard } from '@/components/profile/CompanyQuickLinksCard';
import {
  Loader2, Pencil, Save, X, Building2, Globe, MapPin, Key, ExternalLink, CheckCircle2
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const SECTORS = ['mining', 'processing', 'infrastructure', 'industrial', 'services', 'other'] as const;
const COMPANY_TYPES = ['us_company', 'congolese_sme', 'multinational', 'other'] as const;
const COUNTRIES = [
  { value: 'US', label: 'United States' },
  { value: 'CD', label: 'DR Congo' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'AU', label: 'Australia' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'CN', label: 'China' },
];

interface CompanyData {
  id: string;
  name: string;
  country: string;
  website: string | null;
  description: string | null;
  sectors: string[];
  spa_linked: boolean;
  spa_role_description: string | null;
  logo_url: string | null;
  company_type: string | null;
  verified: boolean;
}

export default function CompanyProfile() {
  const { t } = useTranslation();
  const { user, profile, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState<CompanyData | null>(null);

  // Editable fields
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [sectors, setSectors] = useState<string[]>([]);
  const [spaLinked, setSpaLinked] = useState(false);
  const [spaRoleDescription, setSpaRoleDescription] = useState('');
  const [companyType, setCompanyType] = useState('');

  // Password
  const [changingPassword, setChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || role !== 'company')) {
      navigate('/auth');
      return;
    }
    if (user) fetchCompany();
  }, [user, role, authLoading]);

  const fetchCompany = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (data) {
        setCompany({
          id: data.id,
          name: data.name,
          country: data.country,
          website: data.website,
          description: data.description,
          sectors: (data.sectors as string[]) || [],
          spa_linked: data.spa_linked,
          spa_role_description: data.spa_role_description,
          logo_url: data.logo_url,
          company_type: data.company_type,
          verified: data.verified,
        });
      }
    } catch (e) {
      // No company profile
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    if (!company) return;
    setName(company.name);
    setCountry(company.country);
    setWebsite(company.website || '');
    setDescription(company.description || '');
    setSectors([...company.sectors]);
    setSpaLinked(company.spa_linked);
    setSpaRoleDescription(company.spa_role_description || '');
    setCompanyType(company.company_type || '');
    setEditing(true);
  };

  const toggleSector = (sector: string) => {
    setSectors(prev => prev.includes(sector) ? prev.filter(s => s !== sector) : [...prev, sector]);
  };

  const handleSave = async () => {
    if (!user || !company) return;
    setSaving(true);
    try {
      const updates = {
        name,
        country,
        website: website || null,
        description: description || null,
        sectors,
        spa_linked: spaLinked,
        spa_role_description: spaLinked ? spaRoleDescription : null,
        company_type: companyType || null,
      };

      const { error } = await supabase.from('companies').update(updates).eq('id', company.id);
      if (error) throw error;

      setCompany(prev => prev ? { ...prev, ...updates } : null);
      setEditing(false);
      toast({ title: t('myProfile.saveSuccess') });
    } catch (error) {
      console.error(error);
      toast({ title: t('myProfile.saveError'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (newPassword.length < 8) {
      toast({ title: t('auth.errors.passwordTooShort'), variant: 'destructive' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: t('auth.errors.passwordsDontMatch'), variant: 'destructive' });
      return;
    }
    setPasswordSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast({ title: t('auth.passwordUpdated') });
      setChangingPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast({ title: error.message || t('auth.errors.genericError'), variant: 'destructive' });
    } finally {
      setPasswordSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <Layout>
        <div className="container flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!company) {
    return (
      <Layout>
        <div className="container max-w-2xl py-12 text-center">
          <h1 className="font-heading text-2xl font-bold mb-4">{t('company.profile.title')}</h1>
          <p className="text-muted-foreground mb-6">{t('company.dashboard.setupPrompt')}</p>
          <Button onClick={() => navigate('/profile/company/setup')}>
            {t('company.dashboard.setupButton')}
          </Button>
        </div>
      </Layout>
    );
  }

  const countryLabel = COUNTRIES.find(c => c.value === company.country)?.label || company.country;

  return (
    <Layout>
      <div className="container max-w-3xl py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-bold">{t('company.profile.title')}</h1>
        </div>

        {/* Completeness */}
        <CompanyCompleteness company={company} />

        {/* Edit toggle */}
        <div className="flex items-center justify-end">
          {!editing ? (
            <Button variant="outline" onClick={startEditing}>
              <Pencil className="mr-2 h-4 w-4" />
              {t('common.edit')}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setEditing(false)}>
                <X className="mr-1 h-4 w-4" />
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                {t('common.save')}
              </Button>
            </div>
          )}
        </div>

        {/* Account Info */}
        {profile && role && user && (
          <AccountInfoCard profile={{ email: profile.email, created_at: user.created_at, language: profile.language }} role={role} />
        )}

        {/* Company Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="h-5 w-5 text-primary" />
              {t('companyProfile.companyInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {editing ? (
              <>
                {/* Logo */}
                <div className="flex items-center gap-4">
                  <CompanyLogoUpload
                    companyId={company.id}
                    companyName={company.name}
                    logoUrl={company.logo_url}
                    onUploaded={(url) => setCompany(prev => prev ? { ...prev, logo_url: url } : null)}
                  />
                  <p className="text-xs text-muted-foreground">{t('companyProfile.logoHint')}</p>
                </div>

                <div className="space-y-2">
                  <Label>{t('company.profile.name')} *</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} required />
                </div>

                <div className="space-y-2">
                  <Label>{t('companyProfile.companyType')}</Label>
                  <Select value={companyType} onValueChange={setCompanyType}>
                    <SelectTrigger><SelectValue placeholder={t('companyProfile.selectType')} /></SelectTrigger>
                    <SelectContent>
                      {COMPANY_TYPES.map(ct => (
                        <SelectItem key={ct} value={ct}>{t(`companyProfile.types.${ct}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('company.profile.country')} *</Label>
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('company.profile.website')}</Label>
                  <Input type="url" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://" />
                </div>

                <div className="space-y-2">
                  <Label>{t('company.profile.description')}</Label>
                  <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} />
                </div>

                <div className="space-y-3">
                  <Label>{t('company.profile.sectors')}</Label>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {SECTORS.map(sector => (
                      <Label
                        key={sector}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border p-3 transition-colors ${
                          sectors.includes(sector) ? 'border-primary bg-primary/5' : 'border-muted'
                        }`}
                      >
                        <Checkbox checked={sectors.includes(sector)} onCheckedChange={() => toggleSector(sector)} />
                        <span className="text-sm">{t(`sectors.${sector}`)}</span>
                      </Label>
                    ))}
                  </div>
                </div>

                <div className="space-y-4 rounded-lg border p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox id="spaLinked" checked={spaLinked} onCheckedChange={(c) => setSpaLinked(c as boolean)} />
                    <Label htmlFor="spaLinked" className="cursor-pointer leading-tight">{t('company.profile.spaLinked')}</Label>
                  </div>
                  {spaLinked && (
                    <div className="space-y-2">
                      <Label>{t('company.profile.spaRole')}</Label>
                      <Textarea value={spaRoleDescription} onChange={e => setSpaRoleDescription(e.target.value)} rows={3} />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* View mode */}
                <div className="flex items-center gap-4">
                  <CompanyLogoUpload
                    companyId={company.id}
                    companyName={company.name}
                    logoUrl={company.logo_url}
                    onUploaded={(url) => setCompany(prev => prev ? { ...prev, logo_url: url } : null)}
                  />
                  <div>
                    <h2 className="text-xl font-bold">{company.name}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      {company.verified && (
                        <Badge variant="secondary" className="gap-1 bg-green-100 text-green-800">
                          <CheckCircle2 className="h-3 w-3" /> {t('companies.verifiedMember')}
                        </Badge>
                      )}
                      {company.company_type && (
                        <div>
                          <Badge variant="outline">{t(`companyProfile.types.${company.company_type}`)}</Badge>
                          <p className="mt-1 text-xs text-muted-foreground">{t(`companyProfile.types.${company.company_type}_desc`)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{countryLabel}</span>
                  </div>
                  {company.website && (
                    <div className="flex items-center gap-2 text-sm">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                        {company.website} <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>

                {company.description && (
                  <p className="text-sm text-muted-foreground">{company.description}</p>
                )}

                {company.sectors.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {company.sectors.map(s => (
                      <SectorBadge key={s} sector={s as any} />
                    ))}
                  </div>
                )}

                {company.spa_linked && (
                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                    <p className="text-sm font-medium text-primary mb-1">🤝 {t('companies.spaInvolvement')}</p>
                    {company.spa_role_description && (
                      <p className="text-sm text-muted-foreground">{company.spa_role_description}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <CompanyStatsCard companyId={company.id} />

        {/* Quick Links */}
        <CompanyQuickLinksCard />

        {/* Security / Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Key className="h-5 w-5 text-primary" />
              {t('myProfile.security')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">{t('myProfile.securityDescription')}</p>
            {!changingPassword ? (
              <Button variant="outline" onClick={() => setChangingPassword(true)}>
                {t('myProfile.changePassword')}
              </Button>
            ) : (
              <div className="space-y-4 max-w-sm">
                <div className="space-y-2">
                  <Label>{t('auth.newPassword')}</Label>
                  <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>{t('auth.confirmNewPassword')}</Label>
                  <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handlePasswordReset} disabled={passwordSaving}>
                    {passwordSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('auth.updatePassword')}
                  </Button>
                  <Button variant="ghost" onClick={() => { setChangingPassword(false); setNewPassword(''); setConfirmPassword(''); }}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
